import { type Authentication } from './types.ts';
import {
    type AuthenticationConverter,
    AuthenticationError,
    type AuthenticationManager,
    type ServerAuthenticationFailureHandler,
    type ServerAuthenticationSuccessHandler
} from './types.ts';
import { serverAuthenticationEntryPointFailureHandler } from './entry-point-failure-handler.ts';
import { httpBasicEntryPoint } from './http-basic-entry-point.ts';
import { httpBasicAuthenticationConverter } from './http-basic-converter.ts';
import { anyExchange, type ServerWebExchangeMatcher } from '../util/matchers.ts';
import { AsyncStorageSecurityContextHolder, type SecurityContext } from './security-context.ts';
import { AsyncLocalStorage } from 'node:async_hooks';
import type { ServerWebExchange } from '../../../types/web/server'
import type { WebFilter, WebFilterChain } from '../../web/server/types.ts';

async function authenticate(exchange: ServerWebExchange,
                            chain: WebFilterChain,
                            token: Authentication,
                            managerResolver: (exchange: ServerWebExchange) => Promise<AuthenticationManager | undefined>,
                            successHandler: ServerAuthenticationSuccessHandler,
                            storage: AsyncLocalStorage<{ securityContext?: Promise<SecurityContext> }>): Promise<void> {
    const authManager = await managerResolver(exchange);
    const authentication = await authManager?.(token);
    if (authentication === undefined) {
        throw new Error("No authentication manager found for the exchange");
    }
    try {
        await onAuthenticationSuccess(authentication, {exchange, chain}, successHandler, storage);
    } catch (e) {
        if (e instanceof AuthenticationError) {
            // todo debug log
        }
        throw e;
    }
}

async function onAuthenticationSuccess(authentication: Authentication,
                                       filterExchange: {exchange: ServerWebExchange, chain: WebFilterChain},
                                       successHandler: ServerAuthenticationSuccessHandler,
                                       storage: AsyncLocalStorage<{ securityContext?: Promise<SecurityContext> }>): Promise<void> {
    AsyncStorageSecurityContextHolder.withAuthentication(authentication)(storage);
    await successHandler(filterExchange, authentication);

}

export type AuthenticationFilterOptions = {
    storage: AsyncLocalStorage<{securityContext?: Promise<SecurityContext>}>
    managerResolver?: (exchange: ServerWebExchange) => Promise<AuthenticationManager | undefined>,
    manager?: AuthenticationManager,
    successHandler?: ServerAuthenticationSuccessHandler,
    // converter is a function that converts the exchange to an authentication object
    converter?: AuthenticationConverter,
    failureHandler?: ServerAuthenticationFailureHandler
    // matcher is a function that checks if the exchange must be authenticated
    matcher?: ServerWebExchangeMatcher,
};

export default function authenticationFilter(opts: AuthenticationFilterOptions): WebFilter {
    const auth = {
        matcher: anyExchange,
        successHandler: async({exchange, chain}: {exchange: ServerWebExchange, chain: WebFilterChain}) => {
            await chain.filter(exchange);
        },
        converter: httpBasicAuthenticationConverter({}),
        failureHandler: serverAuthenticationEntryPointFailureHandler({ entryPoint: httpBasicEntryPoint({}) }),
        ...opts
    };
    let managerResolver = auth.managerResolver;
    if (managerResolver === undefined && auth.manager !== undefined) {
        const manager: AuthenticationManager = auth.manager;
        managerResolver = async (_exchange: ServerWebExchange) => {
            return manager;
        };
    }
    if (managerResolver === undefined) {
        throw new Error("Authentication filter requires a managerResolver or a manager");
    }
    return {
        filter: async (exchange: ServerWebExchange, chain: WebFilterChain): Promise<void> => {
            const matchResult = await auth.matcher(exchange);
            const token = matchResult.match ? await auth.converter(exchange) : undefined;
            if (token === undefined) {
                await chain.filter(exchange);
                return;
            }

            try {
                await authenticate(exchange, chain, token, managerResolver, auth.successHandler, auth.storage);

            }
            catch (error) {
                if (error instanceof AuthenticationError) {
                    await auth.failureHandler({ exchange, chain }, error);
                    return;
                }
                throw error; // Re-throw the error to be handled by the next middleware or error handler
            }
        }
    };
}
